import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { HoneypotField, readHoneypot, submitErrorMessage } from "@/components/HoneypotField";
import { useSettings } from "@/context/SettingsContext";
import { 
  MapPin, Maximize, BedDouble, Bath, Home, 
  ChevronLeft, ChevronRight, Phone, Mail,
  Calendar, Share2, Heart, Check
} from "lucide-react";

export const PropertyPage = () => {
  const { id } = useParams();
  const { settings } = useSettings();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${id}`);
        setProperty(response.data);
        // Set default message
        setContactForm(prev => ({
          ...prev,
          message: `Bonjour,\n\nJe suis intéressé(e) par le bien "${response.data.title}" (Réf: ${response.data.id.slice(0, 8)}).\n\nMerci de me contacter pour organiser une visite.`
        }));
      } catch (error) {
        console.error("Error fetching property:", error);
        toast.error("Impossible de charger ce bien");
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);
  
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const website = readHoneypot(e);
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contacts`, {
        ...contactForm,
        website,
        subject: `Demande concernant: ${property.title}`,
        property_id: property.id
      });
      toast.success("Votre demande a été envoyée avec succès !");
      setContactForm({
        name: "",
        email: "",
        phone: "",
        message: `Bonjour,\n\nJe suis intéressé(e) par le bien "${property.title}".\n\nMerci de me contacter.`
      });
    } catch (error) {
      toast.error(submitErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };
  
  const formatPrice = (price, type) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price) + (type === 'location' ? '/mois' : '');
  };
  
  const getCategoryLabel = (category) => {
    const labels = {
      maison: 'Maison',
      appartement: 'Appartement',
      terrain: 'Terrain',
      garage: 'Garage',
      parking: 'Parking',
      commercial: 'Local commercial'
    };
    return labels[category] || category;
  };
  
  const nextImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    }
  };
  
  const prevImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen" data-testid="property-page-loading">
        <Header />
        <div className="container-custom py-20">
          <div className="skeleton h-[500px] mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-4">
              <div className="skeleton h-10 w-3/4" />
              <div className="skeleton h-6 w-1/2" />
              <div className="skeleton h-40" />
            </div>
            <div className="skeleton h-96" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  if (!property) {
    return (
      <div className="min-h-screen" data-testid="property-not-found">
        <Header />
        <div className="container-custom py-20 text-center">
          <h1 className="font-serif text-3xl mb-4">Bien non trouvé</h1>
          <p className="text-gray-600 mb-8">Ce bien n'existe pas ou n'est plus disponible.</p>
          <Link to="/catalogue" className="btn-primary">
            Retour au catalogue
          </Link>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white" data-testid="property-page">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-[#F9F9F9] py-4 border-b border-gray-100">
        <div className="container-custom">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-[#D4AF37]">Accueil</Link>
            <span className="text-gray-400">/</span>
            <Link to="/catalogue" className="text-gray-500 hover:text-[#D4AF37]">Catalogue</Link>
            <span className="text-gray-400">/</span>
            <span className="text-[#0A0A0A] font-medium truncate">{property.title}</span>
          </div>
        </div>
      </div>
      
      {/* Image Gallery */}
      <section className="relative bg-[#0A0A0A]" data-testid="property-gallery">
        <div className="aspect-[21/9] relative overflow-hidden">
          <img
            src={property.images?.[currentImageIndex] || '/placeholder-bien.svg'}
            alt={property.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          
          {/* Navigation arrows */}
          {property.images?.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-colors"
                data-testid="gallery-prev"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-colors"
                data-testid="gallery-next"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
          
          {/* Image counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm">
            {currentImageIndex + 1} / {property.images?.length || 1}
          </div>
          
          {/* Actions */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <button className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-colors">
              <Share2 size={18} />
            </button>
            <button className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-colors">
              <Heart size={18} />
            </button>
          </div>
        </div>
        
        {/* Thumbnails */}
        {property.images?.length > 1 && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {property.images.map((img, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-16 h-12 rounded overflow-hidden border-2 transition-all ${
                  index === currentImageIndex ? 'border-[#D4AF37]' : 'border-transparent opacity-70 hover:opacity-100'
                }`}
                data-testid={`gallery-thumb-${index}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </section>
      
      {/* Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Badge className={
                    property.property_type === 'vente' ? 'badge-vente' : 
                    property.property_type === 'location' ? 'badge-location' :
                    property.property_type === 'vendu' ? 'bg-green-500 text-white border-0' :
                    'bg-purple-500 text-white border-0'
                  }>
                    {property.property_type === 'vente' ? 'À vendre' : 
                     property.property_type === 'location' ? 'À louer' :
                     property.property_type === 'vendu' ? 'Vendu' : 'Loué'}
                  </Badge>
                  <Badge variant="outline">{getCategoryLabel(property.category)}</Badge>
                  {property.is_exclusive && (
                    <Badge className="bg-white text-[#0A0A0A] border border-[#D4AF37] uppercase tracking-wider" data-testid="property-exclusive">Exclusivité</Badge>
                  )}
                  {property.is_featured && (
                    <Badge className="bg-[#D4AF37] text-[#0A0A0A] border-0">Coup de cœur</Badge>
                  )}
                </div>
                
                <h1 className="font-serif text-3xl md:text-4xl text-[#0A0A0A] mb-2" data-testid="property-title">
                  {property.title}
                </h1>
                {property.reference && (
                  <p className="text-sm uppercase tracking-wider text-[#B8952B] font-semibold mb-4" data-testid="property-reference">
                    Référence : {property.reference}
                  </p>
                )}
                
                <div className="flex items-center gap-2 text-gray-600 mb-6">
                  <MapPin size={18} className="text-[#D4AF37]" />
                  <span>{property.address}, {property.city} {property.postal_code}</span>
                </div>
                
                <p className="font-serif text-4xl text-[#D4AF37]" data-testid="property-price">
                  {formatPrice(property.price, property.property_type)}
                </p>
              </div>
              
              {/* Key Features */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-y border-gray-100">
                <div className="text-center p-4">
                  <Maximize size={24} className="mx-auto mb-2 text-[#D4AF37]" />
                  <p className="font-semibold text-lg">{property.surface} m²</p>
                  <p className="text-sm text-gray-500">Surface</p>
                </div>
                {property.rooms > 0 && (
                  <div className="text-center p-4">
                    <Home size={24} className="mx-auto mb-2 text-[#D4AF37]" />
                    <p className="font-semibold text-lg">{property.rooms}</p>
                    <p className="text-sm text-gray-500">Pièces</p>
                  </div>
                )}
                {property.bedrooms > 0 && (
                  <div className="text-center p-4">
                    <BedDouble size={24} className="mx-auto mb-2 text-[#D4AF37]" />
                    <p className="font-semibold text-lg">{property.bedrooms}</p>
                    <p className="text-sm text-gray-500">Chambres</p>
                  </div>
                )}
                {property.bathrooms > 0 && (
                  <div className="text-center p-4">
                    <Bath size={24} className="mx-auto mb-2 text-[#D4AF37]" />
                    <p className="font-semibold text-lg">{property.bathrooms}</p>
                    <p className="text-sm text-gray-500">Salle(s) de bain</p>
                  </div>
                )}
              </div>
              
              {/* Description */}
              <div>
                <h2 className="font-serif text-2xl mb-4">Description</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line" data-testid="property-description">
                  {property.description}
                </p>
              </div>
              
              {/* Features */}
              {property.features?.length > 0 && (
                <div>
                  <h2 className="font-serif text-2xl mb-4">Équipements & prestations</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {property.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 text-gray-600">
                        <Check size={16} className="text-[#D4AF37]" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Reference */}
              <div className="text-sm text-gray-500">
                Référence: {property.id.slice(0, 8).toUpperCase()}
              </div>
            </div>
            
            {/* Sidebar - Contact Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-[#F9F9F9] p-6 rounded-sm" data-testid="contact-form-sidebar">
                <h3 className="font-serif text-xl mb-6">Demander une visite</h3>
                
                <form onSubmit={handleContactSubmit} className="space-y-4" style={{ position: "relative" }}>
                  <HoneypotField />
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                      required
                      data-testid="contact-message-input"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                  >
                    {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
                  </Button>
                </form>
                
                {/* Direct contact */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-500 mb-4">Ou contactez-nous directement</p>
                  <div className="space-y-3">
                    <a 
                      href={`tel:${settings.phone?.replace(/\s/g, '')}`}
                      className="flex items-center gap-3 text-gray-700 hover:text-[#D4AF37] transition-colors"
                    >
                      <Phone size={18} />
                      <span>{settings.phone}</span>
                    </a>
                    <a 
                      href={`mailto:${settings.email}`}
                      className="flex items-center gap-3 text-gray-700 hover:text-[#D4AF37] transition-colors"
                    >
                      <Mail size={18} />
                      <span>{settings.email}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PropertyPage;
